// Доменные типы и помощники для раздела «Конфигурация».
// Эти DTO живут только в бекофисе (бэкенд отдаёт ровно эти поля для club-scoped
// REST-эндпоинтов). Деньги/числа — number, время — строки 'HH:MM', daysMask — int.

// ─────────────────────────── Тарифы ───────────────────────────
export interface Tariff {
  id: string
  name: string
  pricePerHour: number
  color?: string | null
  timeFrom?: string | null
  timeTo?: string | null
  daysMask?: number | null
}

// ─────────────────────────── Типы игр ───────────────────────────
/** Справочник типов игр клуба (заводится здесь, синкается на терминал). */
export interface GameType {
  id: string
  /** стабильный код (генерится бэкендом) — на него ссылается стол/бронь */
  code: string
  name: string
  color?: string | null
  position?: number | null
}

// ─────────────────────────── Столы ───────────────────────────
export interface Table {
  id: string
  name: string
  /** контакт реле PowerAnt — буква A–N (до 14 столов) */
  relay: string
  position?: number | null
  tariffId?: string | null
  /** код типа игры (game_types.code); null = универсальный */
  kind?: string | null
  /** доступен ли стол для онлайн-брони (публичный сайт / Telegram Mini App) */
  onlineBookable?: boolean
  /** доступные тарифы для стола (если бэкенд их отдаёт) */
  tariffIds?: string[]
}

// ─────────────────────────── Бронирования ───────────────────────────
export type BookingStatus =
  | 'pending'
  | 'confirmed'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'no_show'

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтверждена',
  active: 'Идёт',
  completed: 'Завершена',
  cancelled: 'Отменена',
  no_show: 'Не пришёл'
}

export const BOOKING_SOURCE_LABEL: Record<string, string> = {
  public: 'Сайт',
  backoffice: 'Бекофис',
  terminal: 'Терминал',
  bookly: 'Bookly',
  telegram: 'Telegram'
}

export interface BookingItem {
  id: string
  inventoryItemId: string
  qty: number
  name?: string | null
}

/** Активная (незакрытая) сессия = стол «в игре» сейчас (синк с терминала). */
export interface ActiveSession {
  id: string
  tableId: string | null
  startedAt: number
  plannedMinutes: number | null
  customerName: string | null
}

export interface Booking {
  id: string
  tableId?: string | null
  /** код типа игры (game_types.code); null = любой стол */
  gameKind?: string | null
  customerId?: string | null
  customerName: string
  phone: string
  startsAt: number
  endsAt: number
  status: BookingStatus
  source: string
  notes?: string | null
  items?: BookingItem[]
}

// ─────────────────────── Лояльность: уровни ───────────────────────
export interface LoyaltyLevel {
  id: string
  name: string
  threshold: number
  discountPercent: number
  bonusPercent: number
  color?: string | null
  position?: number | null
  /** Двойная логика скидок (вариант Б): распространять ли % скидки уровня на бар/кухню. */
  barDiscountEnabled?: boolean
}

// ─────────────────────── Лояльность: промо ───────────────────────
export interface LoyaltyPromo {
  id: string
  name: string
  discountPercent: number
  bonusPercent: number
  timeFrom?: string | null
  timeTo?: string | null
  daysMask?: number | null
  activeFrom?: number | null
  activeTo?: number | null
  isActive?: boolean
  /** Двойная логика скидок (вариант Б): распространять ли % скидки акции на бар/кухню. */
  barDiscountEnabled?: boolean
  /** Лимит применений (1…10 000 000); null — без ограничения. */
  usageLimit?: number | null
  /** Сколько раз акция применена (счётчик ведёт терминал). */
  usedCount?: number
}

// ─────────────────────────── Купоны ───────────────────────────
export type CouponKind = 'percent' | 'free_time'

/** Купон: скидка % на чек ИЛИ бесплатное игровое время (минуты). */
export interface Coupon {
  id: string
  /** короткий код для ввода кассиром (генерируется бэкендом при выдаче) */
  code: string
  kind: CouponKind
  discountPercent: number
  freeMinutes: number
  note?: string | null
  isActive: boolean
  issuedAt: number
  expiresAt?: number | null
  redeemedAt?: number | null
  redeemedSessionId?: string | null
  /** Двойная логика скидок (вариант Б): распространять ли % скидки купона на бар/кухню. */
  barDiscountEnabled?: boolean
  /** Сколько раз купон можно применить (по умолчанию 1 — одноразовый). */
  usageLimit?: number
  /** Сколько раз уже применён; исчерпан при usedCount >= usageLimit. */
  usedCount?: number
}

// ─────────────────────────── Настройки ───────────────────────────
export interface SettingRow {
  key: string
  value: string
}

/** Ключ настройки: порядок контактов реле PowerAnt (JSON-массив букв). */
export const RELAY_LETTERS_KEY = 'relayLetters'
/** Стандартный набор контактов PowerAnt (8 столов), если порядок не задан. */
export const DEFAULT_RELAY_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']

/** Парсит сохранённый порядок реле (JSON-массив букв); пусто/битое → стандарт A–H. */
export function parseRelayLetters(raw?: string | null): string[] {
  if (!raw) return [...DEFAULT_RELAY_LETTERS]
  try {
    const arr: unknown = JSON.parse(raw)
    if (Array.isArray(arr)) {
      const letters = arr
        .map((x) => String(x).trim().toUpperCase())
        .filter((x) => /^[A-Z]$/.test(x))
      if (letters.length > 0) return letters
    }
  } catch {
    // битый JSON — используем дефолт
  }
  return [...DEFAULT_RELAY_LETTERS]
}

// ─────────────────────── План зала (миникарта) ───────────────────────
/** Ключ настройки: план зала для миникарты выбора стола (JSON, см. FloorMapLayout). */
export const FLOOR_MAP_KEY = 'floorMap'
/** Дефолтная канва плана зала (абстрактные единицы; те же на бэкенде и клиентах). */
export const DEFAULT_FLOOR_CANVAS = { w: 1000, h: 700 }

/** Позиция стола на плане (центр глифа), rot ∈ {0,90,180,270}, scale ∈ [0.5, 2]. */
export interface FloorMapEntry {
  x: number
  y: number
  rot: number
  scale: number
}

/** Допустимый диапазон индивидуального масштаба стола на плане. */
export const FLOOR_SCALE_MIN = 0.5
export const FLOOR_SCALE_MAX = 2

/** Нормализует масштаб стола: не число/вне диапазона → 1 либо ближайшая граница. */
export function normalizeFloorScale(v: unknown): number {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return 1
  return Math.round(Math.min(Math.max(n, FLOOR_SCALE_MIN), FLOOR_SCALE_MAX) * 10) / 10
}

export interface FloorMapLayout {
  version: 1
  canvas: { w: number; h: number }
  tables: Record<string, FloorMapEntry>
}

/** Парсит сохранённый план зала; пусто/битое → null (план не настроен). */
export function parseFloorMap(raw?: string | null): FloorMapLayout | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as {
      canvas?: { w?: unknown; h?: unknown }
      tables?: Record<string, { x?: unknown; y?: unknown; rot?: unknown; scale?: unknown }>
    }
    const dim = (v: unknown, fallback: number): number =>
      typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback
    const tables: Record<string, FloorMapEntry> = {}
    for (const [id, e] of Object.entries(parsed.tables ?? {})) {
      const x = Number(e?.x)
      const y = Number(e?.y)
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue
      const rotRaw = Number(e?.rot)
      tables[id] = {
        x,
        y,
        rot: [0, 90, 180, 270].includes(rotRaw) ? rotRaw : 0,
        scale: normalizeFloorScale(e?.scale)
      }
    }
    return {
      version: 1,
      canvas: {
        w: dim(parsed.canvas?.w, DEFAULT_FLOOR_CANVAS.w),
        h: dim(parsed.canvas?.h, DEFAULT_FLOOR_CANVAS.h)
      },
      tables
    }
  } catch {
    return null // битый JSON — план не настроен
  }
}

// ─────────────────────── Пользователи (PIN) ───────────────────────
export type PinRole = 'owner' | 'admin' | 'operator'

export interface PinUser {
  id: string
  name: string
  role: PinRole
  /** PIN обычно не возвращается с бэкенда; задаётся при создании/смене */
  pin?: string
  /**
   * С какого терминала приехала учётка (null — создана в бекофисе). По нему видно
   * клонов: после переустановки терминала стандартные учётки могли задвоиться —
   * у копий разный deviceId при одинаковом имени и роли.
   */
  deviceId?: string | null
}

export const PIN_ROLE_LABEL: Record<PinRole, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  operator: 'Оператор'
}

// ─────────────────────────── Клиенты (CRM) ───────────────────────────
export interface Customer {
  id: string
  name: string
  phone?: string | null
  cardCode?: string | null
  qrCode?: string | null
  notes?: string | null
  levelId?: string | null
  bonusBalance?: number
  personalDiscountPercent?: number | null
  personalDiscountNote?: string | null
  /** Двойная логика скидок (вариант Б): распространять ли персональную скидку клиента на бар/кухню. */
  personalDiscountBarEnabled?: boolean
}

/** Применённый клиенту штраф (синк терминала, read-only). Сумма — снимок на момент выписки. */
export interface FineCharge {
  id: string
  customerId: string
  shiftId?: string | null
  userId?: string | null
  name: string
  baseUnits: number
  baseValue: number
  amount: number
  ts: number
  note?: string | null
}

// ════════════════════════ daysMask (bit 0 = Пн … 6 = Вс) ════════════════════════

/** Короткие подписи дней недели в порядке битов (Пн..Вс). */
export const DAY_LABELS: readonly string[] = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

/** Полное число «все дни» (0b1111111 = 127). */
export const ALL_DAYS_MASK = 0b1111111

/** Установлен ли бит дня (0..6) в маске. */
export function dayEnabled(mask: number, dayIndex: number): boolean {
  return (mask & (1 << dayIndex)) !== 0
}

/** Переключить бит дня и вернуть новую маску. */
export function toggleDay(mask: number, dayIndex: number): number {
  return mask ^ (1 << dayIndex)
}

/** Человекочитаемое описание маски: «Ежедневно», «Будни», «Пн, Ср, Пт» и т.п. */
export function describeDaysMask(mask: number | null | undefined): string {
  if (mask == null || mask === 0) return 'Любой день'
  const m = mask & ALL_DAYS_MASK
  if (m === ALL_DAYS_MASK) return 'Ежедневно'
  if (m === 0b0011111) return 'Будни (Пн–Пт)'
  if (m === 0b1100000) return 'Выходные (Сб, Вс)'
  const days: string[] = []
  for (let i = 0; i < 7; i++) if (dayEnabled(m, i)) days.push(DAY_LABELS[i])
  return days.join(', ')
}

/** Описание временного окна 'HH:MM'–'HH:MM' или «круглосуточно». */
export function describeTimeWindow(from?: string | null, to?: string | null): string {
  if (!from && !to) return 'Круглосуточно'
  return `${from || '00:00'}–${to || '24:00'}`
}
